import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { TooltipProvider } from '@shared/components/ui/tooltip';
import { Toaster } from '@shared/components/ui/toaster';
import { Toaster as Sonner } from '@shared/components/ui/sonner';
import { TenantProvider, useTenant } from '@core/tenant/TenantProvider';
import { useRestaurantTheme } from '@core/tenant/useRestaurantTheme';
import { AuthProvider, useAuth } from '@core/auth';
import { ProfileCompletionEnforcer } from '@core/auth/ProfileCompletionEnforcer';
import { I18nProvider } from '@core/i18n';
import { AppTextsInjector } from '@core/i18n/AppTextsInjector';
import { queryClient } from '@core/queryClient';
import ScrollToTop from '@shared/components/common/ScrollToTop';
import { AuthErrorToast } from '@shared/components/common/AuthErrorToast';

interface AppProvidersProps {
  children: React.ReactNode;
}

/**
 * Inner providers that depend on TenantProvider
 */
function SentryContextSetter() {
  const { restaurant } = useTenant();
  const { authState } = useAuth();

  useEffect(() => {
    if (restaurant) {
      Sentry.setTag('restaurant.slug', restaurant.subdomainPrefix);
      Sentry.setTag('restaurant.id', String(restaurant.id));
      Sentry.setContext('restaurant', {
        id: restaurant.id,
        name: restaurant.name,
        subdomain: restaurant.subdomainPrefix,
      });
    }
  }, [restaurant]);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      Sentry.setUser({
        id: authState.userId,
        email: authState.email,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [authState?.isAuthenticated, authState?.userId, authState?.email]);

  return null;
}

function TenantAwareProviders({ children }: { children: React.ReactNode }) {
  useRestaurantTheme();

  return (
    <AuthProvider>
      <SentryContextSetter />
      <I18nProvider>
        <AppTextsInjector />
        <AuthErrorToast />
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <ProfileCompletionEnforcer />
            {children}
          </BrowserRouter>
        </TooltipProvider>
      </I18nProvider>
    </AuthProvider>
  );
}

/**
 * Combined providers wrapper for the application
 *
 * Order matters:
 * 1. QueryClientProvider - React Query for server state
 * 2. TenantProvider - Multi-tenant context (depends on QueryClient)
 * 3. AuthProvider - User authentication state
 * 4. I18nProvider - Internationalization (depends on Tenant for default language)
 * 5. TooltipProvider - UI tooltips
 * 6. BrowserRouter - React Router (above TenantGuard so routing always works)
 *
 * TenantGuard, AnalyticsProvider and BookingProvider are applied per-route
 * in router.tsx via layout routes, NOT here. This allows routes like
 * /auth-return to work even when the restaurant API times out.
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <TenantAwareProviders>
          {children}
        </TenantAwareProviders>
      </TenantProvider>
    </QueryClientProvider>
  );
}

export default AppProviders;
